import React, { useState, useRef, useEffect } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const RADIO_DEFAULT = 70;       // m — ~1,5 ha
const RADIO_MIN = 20;
const PASOS = 64;               // vértices que aproximan el círculo

const rad = (d) => (d * Math.PI) / 180;

// Círculo geodésico aproximado como anillo de PASOS puntos [lng,lat].
function circuloAAnillo([lng, lat], radioM) {
  const dLat = radioM / 111320;
  const dLng = radioM / (111320 * Math.cos(rad(lat)));
  const anillo = [];
  for (let i = 0; i < PASOS; i++) {
    const t = (i / PASOS) * 2 * Math.PI;
    anillo.push([lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return anillo;
}

/**
 * El cliente marca el centro de su propiedad y ajusta un círculo para cubrirla.
 * onChange({ poligono: [[lng,lat],...] | null, areaHa: number, superaMax: boolean })
 * maxHa: 8 para el plan individual (default), 50 para Plan Comunal — debe coincidir con
 * MAX_M2_SELF_SERVE / MAX_M2_COMUNAL en el backend (services/pricing.js).
 */
export default function DibujarPredio({ onChange, dm, altura = 260, maxHa = 8 }) {
  const [centro, setCentro] = useState(null);      // [lng, lat]
  const [radio, setRadio] = useState(RADIO_DEFAULT);
  const [view, setView] = useState({ longitude: -70.65, latitude: -33.45, zoom: 5 });
  const [busqueda, setBusqueda] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [errBusqueda, setErrBusqueda] = useState('');
  const mapRef = useRef(null);

  const accent = dm ? dm.accent : '#15704f';
  const muted = dm ? dm.muted : '#8b7355';
  const border = dm ? dm.border : '#d4c9a8';

  // El slider llega hasta 2,5x el radio del tope, para que se alcance a ver el aviso de
  // "supera las X ha" en vez de topar el slider justo en el límite.
  const radioMax = Math.round(Math.sqrt((maxHa * 10000) / Math.PI) * 2.5);

  const areaM2 = Math.PI * radio * radio;
  const ha = areaM2 / 10000;
  const superaMax = ha > maxHa;
  const listo = !!centro;

  useEffect(() => {
    onChange?.({
      poligono: listo ? circuloAAnillo(centro, radio) : null,
      areaHa: ha,
      superaMax: listo && superaMax,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centro, radio]);

  const onMapClick = (evt) => {
    setCentro([evt.lngLat.lng, evt.lngLat.lat]);
  };

  const buscarDireccion = async () => {
    if (!busqueda.trim()) return;
    setBuscando(true); setErrBusqueda('');
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(busqueda)}.json?country=CL&language=es&access_token=${MAPBOX_TOKEN}`;
      const data = await fetch(url).then((r) => r.json());
      if (data.features?.length > 0) {
        const [lon, lat] = data.features[0].center;
        setView({ longitude: lon, latitude: lat, zoom: 16 });
        mapRef.current?.flyTo({ center: [lon, lat], zoom: 16, duration: 800 });
      } else setErrBusqueda('No se encontró la dirección.');
    } catch { setErrBusqueda('Error buscando la dirección.'); }
    finally { setBuscando(false); }
  };

  const anillo = listo ? circuloAAnillo(centro, radio) : [];
  const geojson = { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...anillo, anillo[0]]] } };
  const color = superaMax ? '#d69e2e' : accent;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input
          className="l-field"
          style={{ flex: 1, marginBottom: 0 }}
          placeholder="Busca tu dirección o localidad..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), buscarDireccion())}
        />
        <button type="button" onClick={buscarDireccion} disabled={buscando}
          style={{ padding: '9px 12px', background: accent, color: '#fff', border: 'none', borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {buscando ? '...' : 'Buscar'}
        </button>
      </div>
      {errBusqueda && <div style={{ fontSize: 11, color: '#c53030', marginBottom: 6 }}>{errBusqueda}</div>}

      <div style={{ fontSize: 11.5, color: muted, lineHeight: 1.5, marginBottom: 6 }}>
        {listo
          ? 'Ajusta el tamaño con la barra para que el círculo cubra toda tu propiedad. Lo que quede fuera no recibe alertas — no lo achiques para pagar menos o para calificar al plan.'
          : 'Toca el mapa en el centro de tu propiedad.'}
      </div>

      <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 8, border: '1.5px solid ' + (listo ? accent : border), height: altura }}>
        <Map
          ref={mapRef}
          {...view}
          onMove={(e) => setView(e.viewState)}
          onClick={onMapClick}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          cursor="crosshair"
        >
          {listo && (
            <>
              <Source id="predio-circ" type="geojson" data={geojson}>
                <Layer id="predio-fill" type="fill" paint={{ 'fill-color': color, 'fill-opacity': 0.22 }} />
                <Layer id="predio-line" type="line" paint={{ 'line-color': color, 'line-width': 2 }} />
              </Source>
              <Marker longitude={centro[0]} latitude={centro[1]} anchor="center">
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.4)' }} />
              </Marker>
            </>
          )}
        </Map>
      </div>

      {listo && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <input
              type="range"
              min={RADIO_MIN}
              max={radioMax}
              step={5}
              value={radio}
              onChange={(e) => setRadio(parseInt(e.target.value))}
              style={{ flex: 1, accentColor: color }}
            />
            <div style={{ fontSize: 13, fontWeight: 700, color: superaMax ? '#b7791f' : accent, whiteSpace: 'nowrap' }}>
              ≈ {ha.toLocaleString('es-CL', { maximumFractionDigits: 2 })} ha
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: muted }}>radio {radio} m — toca el mapa para mover el centro</span>
            <button type="button" onClick={() => { setCentro(null); setRadio(RADIO_DEFAULT); }}
              style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 8, border: '1.5px solid ' + border, background: 'none', color: muted, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
              Reiniciar
            </button>
          </div>
        </>
      )}

      {listo && superaMax && (
        <div style={{ background: '#fff8e8', border: '1px solid #d69e2e', borderRadius: 9, padding: '8px 12px', fontSize: 12, color: '#8b6914', lineHeight: 1.6, marginBottom: 4 }}>
          Tu propiedad supera las {maxHa} ha. Necesita varias zonas de monitoreo —{' '}
          <a href="mailto:contacto@montaraz.cl" style={{ color: accent, fontWeight: 600 }}>escríbenos</a>{' '}
          para un plan a medida.
        </div>
      )}
    </div>
  );
}
